import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useLanguage } from './LanguageContext';
import type { Database } from '../lib/database.types';

type CountryTranslationRow = Database['public']['Tables']['country_translations']['Row'];

interface CountryTranslation {
  country_code: string;
  name_en: string;
  name_fr: string;
  name_ar: string;
  flag: string;
}

interface CountryTranslationsContextType {
  getCountryName: (countryCode: string) => string;
  loading: boolean;
  translations: Map<string, CountryTranslation>;
}

const CountryTranslationsContext = createContext<CountryTranslationsContextType | undefined>(undefined);

interface CountryTranslationsProviderProps {
  children: React.ReactNode;
}

export function CountryTranslationsProvider({ children }: CountryTranslationsProviderProps) {
  const { language } = useLanguage();
  const [translations, setTranslations] = useState<Map<string, CountryTranslation>>(new Map());
  const [loading, setLoading] = useState(true);

  const fetchTranslations = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('country_translations')
        .select('*');

      if (error) throw error;

      const translationsMap = new Map<string, CountryTranslation>();
      (data || []).forEach((translation) => {
        translationsMap.set(translation.country_code, translation);
      });

      setTranslations(translationsMap);
    } catch (err) {
      // Silently fail - translations will remain empty, components will use country codes as fallback
      console.error('Failed to fetch country translations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTranslations();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('country-translations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'country_translations',
        },
        () => {
          fetchTranslations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchTranslations]);

  const getCountryName = useCallback(
    (countryCode: string): string => {
      const translation = translations.get(countryCode);
      if (!translation) return countryCode; // Fallback to code if translation not found

      switch (language) {
        case 'fr':
          return translation.name_fr;
        case 'ar':
          return translation.name_ar;
        case 'en':
        default:
          return translation.name_en;
      }
    },
    [language, translations]
  );

  return (
    <CountryTranslationsContext.Provider value={{ getCountryName, loading, translations }}>
      {children}
    </CountryTranslationsContext.Provider>
  );
}

export function useCountryTranslations() {
  const context = useContext(CountryTranslationsContext);
  if (context === undefined) {
    throw new Error('useCountryTranslations must be used within a CountryTranslationsProvider');
  }
  return context;
}

